import {
    appData,
    tempConfigData,
    tempTeamsData,
    setTempTeamsData,
} from '../../app/state/state.js';
import { db } from '../../app/firebase/firebase.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import {
    getNotificationSettingsGroups,
    NOTIFICATION_SETTINGS_SURFACES,
} from '../../app/notifications/notificationSettingsModel.js';
import {
    getRequiredTeamNotificationConfigEntries,
} from "../../app/notifications/notificationRegistryAdapter.js";
import {
    escapeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";
import {
    destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import {
    getUserFullDisplayName,
} from "../../shared/utils/userDisplay.js";
import { collection, getDocs } from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

let teamsDirty = false;
let markSettingsDirty = () => {};
let pendingTeamNameFocusId = "";
const TEAM_TRIGGER_NAVIGATION_ORDER = Object.freeze([
    ["team_task_"],
    ["team_patient_"],
    ["team_labs_"],
    ["team_recall_"],
    ["team_project_"],
    ["team_asset_"],
    ["team_maintenance_"],
    ["team_drug_", "drug_"],
]);

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function renderTeamVisibilityButton({team}) {
    const isVisible = team.visible !== false;
    const icon = isVisible ? "visibility" : "visibility_off";
    const stateLabel = isVisible ? "Visible" : "Hidden";
    return `
        <button type="button" class="team-visible-toggle-btn${isVisible ? " is-visible" : ""}" data-team-id="${attrValue(team.id)}" title="Visible" aria-label="${stateLabel}: ${attrValue(team.name || "Unnamed Team")}" aria-pressed="${String(isVisible)}">
            <span class="material-symbols-outlined text-lg leading-none">${icon}</span>
        </button>
    `;
}

function renderTeamOrderButton({teamId, direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move up" : "Move down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="move-team-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-team-id="${attrValue(teamId)}" data-direction="${direction}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function markTeamsDirty() {
    teamsDirty = true;
    markSettingsDirty(true);
}

/**
 * Registers the save-button dirty state callback for team settings.
 *
 * @param {Function} handler Dirty-state callback.
 * @returns {void}
 */
export function setTeamSettingsUpdateSaveButton(handler) {
    markSettingsDirty = handler || (() => {});
}

/**
 * Loads shared teams into app and temp state.
 *
 * @returns {Promise<void>}
 */
export async function fetchSharedTeams() {
    try {
        const teamSnapshot = await getDocs(collection(db, "teams"));
        appData.teams = [];

        teamSnapshot.forEach((teamDoc) => {
            appData.teams.push({ id: teamDoc.id, ...teamDoc.data() });
        });

        if (tempTeamsData === null) {
            setTempTeamsData(appData.teams);
            teamsDirty = false;
        }
    } catch (e) {
        console.error("Error fetching teams:", e);
    }
}

/**
 * Syncs temp team state from live data and rerenders when needed.
 *
 * @param {Function} renderCurrentTabContent Settings tab rerender callback.
 * @returns {void}
 */
export function syncTeamsSettingsFromLiveData(renderCurrentTabContent) {
    if (!Array.isArray(appData.teams) || teamsDirty) {return;}

    setTempTeamsData(appData.teams);

    const content = document.getElementById("settings-content");
    if (!content || content.children.length === 0) {return;}

    renderCurrentTabContent?.();
}

/**
 * Clears team-specific dirty state for the settings view.
 *
 * @returns {void}
 */
export function resetTeamSettingsState() {
    teamsDirty = false;
}

/**
 * Returns sorted shared teams for selectors and editors.
 *
 * @param {boolean} [includeHidden=true] Whether hidden teams should be included.
 * @returns {Array<object>} Sorted team list.
 */
export function getSortedSharedTeams(includeHidden = true) {
    const sourceTeams = Array.isArray(tempTeamsData) ? tempTeamsData : [];
    const teams = includeHidden
        ? [...sourceTeams]
        : sourceTeams.filter(team => team.visible !== false);

    teams.sort((a, b) => {
        const orderA = Number.isFinite(Number(a.order)) ? Number(a.order) : 999;
        const orderB = Number.isFinite(Number(b.order)) ? Number(b.order) : 999;
        if (orderA !== orderB) {return orderA - orderB;}
        return (a.name || '').localeCompare(b.name || '');
    });

    return teams;
}

function getNextTeamOrder() {
    const teams = Array.isArray(tempTeamsData) ? tempTeamsData : [];
    return teams.length > 0
        ? Math.max(...teams.map(team => Number.isFinite(Number(team.order)) ? Number(team.order) : 0)) + 1
        : 1;
}

function createTempTeam(name) {
    return {
        id: crypto.randomUUID(),
        name,
        members: [],
        visible: true,
        order: getNextTeamOrder(),
    };
}

function focusPendingTeamNameInput() {
    if (!pendingTeamNameFocusId) {return;}
    const input = document.querySelector(
        `.team-name-input[data-team-id="${CSS.escape(pendingTeamNameFocusId)}"]`,
    );
    pendingTeamNameFocusId = "";
    if (!input) {return;}
    input.focus();
    input.select();
}

function getOrderedTeamEntries() {
    return (Array.isArray(tempTeamsData) ? tempTeamsData : [])
        .map((team, index) => ({
            index,
            team,
            order: Number(team?.order ?? index + 1) || index + 1,
            name: team?.name || '',
        }))
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.name.localeCompare(b.name);
        });
}

function moveTeam(teamId, direction) {
    const entries = getOrderedTeamEntries();
    const currentPosition = entries.findIndex((entry) => entry.team?.id === teamId);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;

    if (
        currentPosition < 0 ||
        nextPosition < 0 ||
        nextPosition >= entries.length
    ) {
        return false;
    }

    entries.forEach((entry, position) => {
        if (typeof entry.team === "object" && entry.team !== null) {
            entry.team.order = position + 1;
        }
    });

    const current = entries[currentPosition].team;
    const next = entries[nextPosition].team;
    const currentOrder = current.order;
    current.order = next.order;
    next.order = currentOrder;
    return true;
}

function getTeamUserOptions(selectedValues = []) {
    return createUserOptions(appData.users || [], {
        selectedValues,
    });
}

function getProviderTeamOptions() {
    return getSortedSharedTeams(true)
        .map((team) => ({
            value: team.id,
            label: team.name || "Unnamed Team",
            searchText: `${team.name || ""} ${team.id || ""}`,
        }));
}

function getTeamMemberSummary(team) {
    const memberIds = Array.isArray(team.members) ? team.members.map(String) : [];
    const names = memberIds
        .map((uid) => {
            const user = (appData.users || []).find((entry) => String(entry.id) === uid);
            return user ? getUserFullDisplayName(user) : uid;
        })
        .sort((a, b) => a.localeCompare(b))
        .filter(Boolean);
    return `Members: ${names.length > 0 ? names.join(', ') : 'None'}`;
}

function mountTeamMemberControls(container) {
    container.querySelectorAll("[data-team-member-control]").forEach((root) => {
        const teamId = root.dataset.teamId;
        const team = tempTeamsData.find((item) => item.id === teamId);
        if (!team) {return;}
        const selectedValues = Array.isArray(team.members) ? team.members : [];
        const options = getTeamUserOptions(selectedValues);

        createSelectControl({
            root,
            name: `team-members-${teamId}`,
            accessibleName: `${team.name || "Team"} members`,
            value: selectedValues,
            options,
            multiple: true,
            searchable: true,
            searchThreshold: 7,
            checkboxMenu: true,
            bulkSelection: true,
            emptySelectionLabel: "No members",
            allSelectedLabel: "All active users",
            selectedCountLabel: "Selected members",
            placeholder: "Select members",
            searchPlaceholder: "Search users",
            onChange: (value) => {
                team.members = Array.isArray(value) ? value : [];
                const summary = root
                    .closest(".team-settings-row")
                    ?.querySelector("[data-team-member-summary]");
                if (summary) {
                    summary.textContent = getTeamMemberSummary(team);
                }
                markTeamsDirty();
            },
        });
    });
}

function mountProviderTeamMirrorControl(scope = document) {
    const root = scope.querySelector?.("#provider-team-mirror-control");
    if (!root) {return;}

    destroyRegisteredControlsIn(root);
    createSelectControl({
        root,
        name: "providerTeamId",
        accessibleName: "Provider team mirror",
        value: tempConfigData?.providerTeamId || "",
        options: getProviderTeamOptions(),
        searchable: true,
        searchThreshold: 7,
        clearable: true,
        placeholder: "Select provider team",
        onChange: (value) => {
            tempConfigData.providerTeamId = value || null;
            markSettingsDirty(true);
        },
    });
}

/**
 * Handles the add-team button action.
 *
 * @returns {boolean} True when the action was handled.
 */
export function handleAddTeamClick() {
    const teams = Array.isArray(tempTeamsData) ? [...tempTeamsData] : [];
    const newTeam = createTempTeam("New Team");
    teams.push(newTeam);
    setTempTeamsData(teams);
    pendingTeamNameFocusId = newTeam.id;
    renderTeamsList();
    renderTeamTriggers();
    mountProviderTeamMirrorControl(document);
    requestAnimationFrame(focusPendingTeamNameInput);
    markTeamsDirty();
    return true;
}

/**
 * Renders the Teams settings tab.
 *
 * @param {HTMLElement} container The tab container element.
 * @returns {void}
 */
export function renderTeamsTab(container) {
    container.innerHTML = `
        <div class="max-w-5xl">
            <div class="bg-white rounded-lg shadow p-6 max-w-5xl">
                <div class="mb-6">
                    <h3 class="text-xl font-bold text-gray-800 mb-2">Manage Shared Teams</h3>
                </div>

                <div id="teams-list-container">
                    <div class="animate-pulse flex space-x-4"><div class="h-10 bg-gray-200 rounded w-full"></div></div>
                </div>
            </div>

            <div class="bg-white rounded-lg shadow p-6 max-w-5xl mt-8">
                <div class="team-provider-mirror-row">
                    <label class="block text-sm font-medium text-gray-700">Provider Team Mirror</label>
                    <div id="provider-team-mirror-control" class="team-provider-mirror-control"></div>
                </div>
                <p class="mt-2 text-sm text-gray-500">When set, users marked as app providers will be mirrored into this shared team. Team edits do not change the provider flag.</p>
            </div>
        </div>
    `;

    if (tempTeamsData === null) {
        fetchSharedTeams()
            .then(() => {
                renderTeamsList();
                mountProviderTeamMirrorControl(container);
            })
            .catch(err => console.error("Failed to load teams:", err));
        return;
    }

    renderTeamsList();
    mountProviderTeamMirrorControl(container);
    bindNativeFieldBehavior(container);
}

/**
 * Handles team-tab button clicks that use delegated events.
 *
 * @param {HTMLElement} target The clicked element.
 * @returns {boolean} True when the click was handled.
 */
export function handleTeamSettingsClick(target) {
    if (target?.closest('#add-team-btn')) {
        return handleAddTeamClick();
    }
    return false;
}

/**
 * Closes team dropdowns when the user clicks outside them.
 *
 * @param {MouseEvent} e The document click event.
 * @returns {void}
 */
export function closeDropdownsOnOutsideClick(e) {
    if (!e.target.closest('.relative-dropdown-container')) {
        const openDropdowns = document.querySelectorAll('.relative-dropdown-container .absolute:not(.hidden)');
        openDropdowns.forEach((el) => {
            el.classList.add('hidden');
        });
    }
}

function initTeamSetting(key) {
    if (!tempConfigData.teamNotificationSettings) {tempConfigData.teamNotificationSettings = {};}
    if (!tempConfigData.teamNotificationSettings[key]) {tempConfigData.teamNotificationSettings[key] = {};}
    const requiredDefaults = getRequiredTeamNotificationConfigEntries()[key];
    if (
        Object.prototype.hasOwnProperty.call(requiredDefaults || {}, "targets") &&
        !Array.isArray(tempConfigData.teamNotificationSettings[key].targets)
    ) {
        tempConfigData.teamNotificationSettings[key].targets = [];
    }
    if (
        Object.prototype.hasOwnProperty.call(requiredDefaults || {}, "countThreshold") &&
        typeof tempConfigData.teamNotificationSettings[key].countThreshold !== "number"
    ) {
        tempConfigData.teamNotificationSettings[key].countThreshold =
            Number(requiredDefaults.countThreshold) || 0;
    }
}

function getTeamNotificationSettingsWithRequiredEvents(mutate = false) {
    const source = tempConfigData.teamNotificationSettings || {};
    const settings = mutate ? source : { ...source };
    const requiredEntries = getRequiredTeamNotificationConfigEntries();

    Object.entries(requiredEntries).forEach(([key, defaults]) => {
        if (!settings[key]) {
            settings[key] = { ...defaults };
        }
    });

    if (mutate) {
        tempConfigData.teamNotificationSettings = settings;
    }

    return settings;
}

function teamTriggerHasDays(key, settings) {
    return Object.prototype.hasOwnProperty.call(
        settings?.[key] || {},
        "daysInAdvance",
    );
}

function teamTriggerHasPriority(key, settings) {
    return Array.isArray(settings?.[key]?.targets);
}

function teamTriggerHasCountThreshold(key, settings) {
    return Object.prototype.hasOwnProperty.call(
        settings?.[key] || {},
        "countThreshold",
    );
}

function getTeamTriggerNavigationRank(key) {
    const eventId = String(key || "");
    const index = TEAM_TRIGGER_NAVIGATION_ORDER.findIndex((prefixes) =>
        prefixes.some((prefix) => eventId.startsWith(prefix))
    );
    return index >= 0 ? index : TEAM_TRIGGER_NAVIGATION_ORDER.length;
}

function orderTeamTriggerKeysByNavigation(keys = []) {
    return keys
        .map((key, index) => ({key, index}))
        .sort((a, b) => {
            const rankDiff =
                getTeamTriggerNavigationRank(a.key) -
                getTeamTriggerNavigationRank(b.key);
            return rankDiff !== 0 ? rankDiff : a.index - b.index;
        })
        .map((entry) => entry.key);
}

function getTeamTriggerTeamOptions() {
    return getSortedSharedTeams(true).map((team) => ({
        value: team.id,
        label: team.name || "Unnamed Team",
        searchText: `${team.name || ""} ${team.id || ""}`,
    }));
}

function getTeamTriggerPriorityOptions() {
    return (tempConfigData.priorities || []).map((priority) => ({
        value: priority.id,
        label: priority.name || priority.id || "Unnamed priority",
        searchText: `${priority.name || ""} ${priority.id || ""}`,
    }));
}

function mountTeamTriggerControls(container) {
    const teamOptions = getTeamTriggerTeamOptions();
    const priorityOptions = getTeamTriggerPriorityOptions();

    container.querySelectorAll("[data-team-trigger-team-control]").forEach((root) => {
        const key = root.dataset.key;
        const config = tempConfigData.teamNotificationSettings?.[key] || {};

        createSelectControl({
            root,
            options: teamOptions,
            value: config.teamId || "",
            searchable: teamOptions.length >= 7,
            searchThreshold: 7,
            portalMenu: true,
            clearable: true,
            placeholder: "Select team",
            emptyMessage: "No teams available.",
            accessibleName: root.dataset.label || "Assigned team",
            mirror: false,
            onChange: (value) => {
                initTeamSetting(key);
                tempConfigData.teamNotificationSettings[key].teamId = value || "";
                markSettingsDirty(true);
            },
        });
    });

    container.querySelectorAll("[data-team-trigger-priority-control]").forEach((root) => {
        const key = root.dataset.key;
        const config = tempConfigData.teamNotificationSettings?.[key] || {};

        createSelectControl({
            root,
            options: priorityOptions,
            value: Array.isArray(config.targets) ? config.targets : [],
            multiple: true,
            searchable: priorityOptions.length >= 7,
            searchThreshold: 7,
            checkboxMenu: true,
            portalMenu: true,
            placeholder: "Select priorities",
            emptySelectionLabel: "No priorities",
            selectedCountLabel: "Selected priorities",
            emptyMessage: "No priorities available.",
            accessibleName: root.dataset.label || "Urgency filter",
            mirror: false,
            onChange: (value) => {
                initTeamSetting(key);
                tempConfigData.teamNotificationSettings[key].targets =
                    Array.isArray(value) ? value : [];
                markSettingsDirty(true);
            },
        });
    });
}

/**
 * Renders the team notification trigger rows used in Notifications settings.
 *
 * @param {Function} [getEventLabel] Event label formatter.
 * @returns {void}
 */
export function renderTeamTriggers(getEventLabel = (key) => key) {
    const container = document.getElementById('team-triggers-table-body');
    if (!container) {return;}

    const settings = getTeamNotificationSettingsWithRequiredEvents(true);
    const keys = orderTeamTriggerKeysByNavigation(getNotificationSettingsGroups(tempConfigData, {
        controls: settings,
        surface: NOTIFICATION_SETTINGS_SURFACES.TEAM,
    }).flatMap((group) => group.events.map((event) => event.id)));

    if (keys.length === 0) {
        destroyRegisteredControlsIn(container);
        container.innerHTML = '<p class="text-sm text-gray-500 p-6 text-center">No team triggers configured.</p>';
        return;
    }

    destroyRegisteredControlsIn(container);
    container.innerHTML = keys.map((key) => {
        const config = settings[key] || {};
        const label = escapeHTML(getEventLabel(key));
        const escapedKey = escapeHTML(key);
        const showDays = teamTriggerHasDays(key, settings);
        const showCountThreshold = teamTriggerHasCountThreshold(key, settings);
        const showPriority = teamTriggerHasPriority(key, settings);

        const renderToggle = (field, isChecked) => `
            <label class="toggle-switch team-notification-toggle-switch">
                <input type="checkbox" class="team-trigger-toggle" ${isChecked ? 'checked' : ''} data-key="${attrValue(key)}" data-field="${field}">
                <span class="slider"></span>
            </label>`;

        return `
        <div class="team-notification-row px-4 py-3 grid min-w-[44rem] grid-cols-[minmax(10rem,1.1fr)_minmax(10rem,1fr)_minmax(12rem,1.15fr)_minmax(5rem,0.6fr)_minmax(7rem,0.75fr)] gap-3 items-center hover:bg-gray-50">
            <div class="team-notification-name min-w-0">
                <div class="font-medium text-sm text-gray-900 truncate" title="${label}">${label}</div>
                <div class="text-[10px] text-gray-400 font-mono truncate">${escapedKey}</div>
            </div>
            
            <div class="team-notification-cell" data-mobile-label="Assigned Team">
                <div
                    data-team-trigger-team-control
                    data-key="${attrValue(key)}"
                    data-label="Assigned team for ${attrValue(getEventLabel(key))}"
                ></div>
            </div>

            <div class="team-notification-cell" data-mobile-label="Urgency Filter">
                ${showPriority ? `
                    <div
                        data-team-trigger-priority-control
                        data-key="${attrValue(key)}"
                        data-label="Urgency filter for ${attrValue(getEventLabel(key))}"
                    ></div>
                ` : '<span class="text-xs text-gray-300">-</span>'}
            </div>

            <div class="team-notification-cell text-center" data-mobile-label="Timing">
                ${showDays ? `
                <div class="flex items-center justify-center space-x-1">
                    <input type="number" class="team-trigger-input w-12 text-center" value="${attrValue(config.daysInAdvance || 0)}" data-key="${attrValue(key)}" data-field="daysInAdvance">
                    <span class="text-[10px] text-gray-500">d</span>
                </div>` : showCountThreshold ? `
                <div class="flex items-center justify-center space-x-1">
                    <input type="number" class="team-trigger-input w-14 text-center" value="${attrValue(config.countThreshold || 0)}" data-key="${attrValue(key)}" data-field="countThreshold">
                    <span class="text-[10px] text-gray-500">drafts</span>
                </div>` : '<span class="text-xs text-gray-300">-</span>'}
            </div>

            <div class="team-notification-alerts team-notification-cell" data-mobile-label="Alerts">
                <div class="team-notification-alert-toggle-list flex justify-center space-x-2">
                    <label class="team-notification-alert-toggle">
                        <span>App</span>
                        ${renderToggle('inApp', config.inApp !== false)}
                    </label>
                    <label class="team-notification-alert-toggle">
                        <span>Email</span>
                        ${renderToggle('email', config.email !== false)}
                    </label>
                </div>
            </div>
        </div>`;
    }).join('');

    mountTeamTriggerControls(container);

    container.querySelectorAll('.team-trigger-input, .team-trigger-toggle').forEach((el) => {
        el.addEventListener('change', (e) => {
            const key = e.target.dataset.key;
            const field = e.target.dataset.field;
            let val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
            if (e.target.type === 'number') {val = parseInt(val, 10) || 0;}

            initTeamSetting(key);
            tempConfigData.teamNotificationSettings[key][field] = val;
            markSettingsDirty(true);
        });
    });

    bindNativeFieldBehavior(container);
}

/**
 * Renders the editable shared teams list.
 *
 * @returns {void}
 */
export function renderTeamsList() {
    const container = document.getElementById('teams-list-container');
    if (!container) {return;}

    const sortedTeams = getSortedSharedTeams(true);

    if (sortedTeams.length === 0) {
        destroyRegisteredControlsIn(container);
        container.innerHTML = `
            <p class="text-sm text-gray-400 italic text-center py-4">No teams created yet.</p>
            <button type="button" id="add-team-btn" class="settings-add-row-btn mt-3">+ Add new team</button>
        `;
        return;
    }

    destroyRegisteredControlsIn(container);
    container.innerHTML = `
        ${sortedTeams.map((team) => {
        const sortedIndex = sortedTeams.findIndex((entry) => entry.id === team.id);
        const isFirst = sortedIndex === 0;
        const isLast = sortedIndex === sortedTeams.length - 1;

        return `
        <div class="team-settings-row-shell">
            <div class="settings-config-row team-settings-row">
                <div class="settings-config-order-controls" aria-label="Team order controls">
                    ${renderTeamOrderButton({teamId: team.id, direction: "up", disabled: isFirst})}
                    ${renderTeamOrderButton({teamId: team.id, direction: "down", disabled: isLast})}
                </div>
                <div class="team-settings-row-main">
                    <input type="text" class="team-name-input settings-config-name" data-team-id="${attrValue(team.id)}" value="${attrValue(team.name || '')}" placeholder="Team name">
                    <div class="team-member-control" data-team-member-control data-team-id="${attrValue(team.id)}"></div>
                    ${renderTeamVisibilityButton({team})}
                    <button class="delete-team-btn settings-config-delete" data-id="${attrValue(team.id)}" title="Delete team" aria-label="Delete team">
                        <span class="material-symbols-outlined text-lg leading-none">delete</span>
                    </button>
                    <div class="team-member-summary text-xs text-gray-500" data-team-member-summary>${escapeHTML(getTeamMemberSummary(team))}</div>
                </div>
            </div>
        </div>`;
    }).join('')}
        <button type="button" id="add-team-btn" class="settings-add-row-btn mt-3">+ Add new team</button>
    `;

    mountTeamMemberControls(container);
    requestAnimationFrame(focusPendingTeamNameInput);

    container.querySelectorAll('.team-name-input').forEach((input) => {
        input.addEventListener('change', (e) => {
            const teamId = e.target.dataset.teamId;
            const name = e.target.value.trim();
            if (!teamId) {return;}
            const team = tempTeamsData.find((item) => item.id === teamId);
            if (!team) {return;}
            if (!name) {
                renderTeamsList();
                renderTeamTriggers();
                return;
            }
            team.name = name;
            renderTeamsList();
            renderTeamTriggers();
            mountProviderTeamMirrorControl(document);
            markTeamsDirty();
        });
    });

    container.querySelectorAll('.move-team-btn').forEach((button) => {
        button.addEventListener('click', (e) => {
            const action = e.target.closest('.move-team-btn');
            const teamId = action?.dataset.teamId;
            const direction = action?.dataset.direction;
            if (!teamId || !direction || !moveTeam(teamId, direction)) {return;}
            renderTeamsList();
            renderTeamTriggers();
            mountProviderTeamMirrorControl(document);
            markTeamsDirty();
        });
    });

    container.querySelectorAll('.team-visible-toggle-btn').forEach((button) => {
        button.addEventListener('click', (e) => {
            const action = e.target.closest('.team-visible-toggle-btn');
            const teamId = action?.dataset.teamId;
            const team = tempTeamsData.find((item) => item.id === teamId);
            if (!team) {return;}
            team.visible = team.visible === false;
            renderTeamsList();
            renderTeamTriggers();
            mountProviderTeamMirrorControl(document);
            markTeamsDirty();
        });
    });

    container.querySelectorAll('.delete-team-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            const teamId = e.target.closest('.delete-team-btn')?.dataset.id;
            openConfirmationModal('Delete Team?', 'Are you sure?', () => {
                const nextTeams = tempTeamsData.filter((team) => team.id !== teamId);
                setTempTeamsData(nextTeams);
                renderTeamsList();
                renderTeamTriggers();
                if (tempConfigData.providerTeamId === teamId) {
                    tempConfigData.providerTeamId = null;
                }
                mountProviderTeamMirrorControl(document);
                markTeamsDirty();
            });
        });
    });
    bindNativeFieldBehavior(container);
}
